import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ExternalLink, Eye, Send, CalendarClock, EyeOff } from 'lucide-react';
import { useAccount } from '../../context/AccountContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import {
  GET_TINYCMS_APPROVALS,
  CREATE_TINYCMS_PREVIEW,
  PUBLISH_TINYCMS_CONTENT,
  UNPUBLISH_TINYCMS_CONTENT,
} from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * Building blocks shared by the TinyCMS admin screens. The screens are a thin
 * client over the same services the agent's MCP tools use, so every rule —
 * approval names the exact version, blocking findings refuse, a later edit
 * stays off the live site — is the server's, not this file's.
 */

export type Kind = 'article' | 'dataset' | 'site' | 'page' | 'form';

export interface Finding {
  source?: string;
  code: string;
  severity: 'blocking' | 'warning';
  path?: string;
  message?: string;
  quote?: string;
  suggestion?: string | null;
  rule?: string | null;
  advisory?: boolean;
}

export interface ItemState {
  live: { version: number; hash: string } | null;
  scheduled: { version: number; at: string } | null;
  hasUnpublishedChanges: boolean;
}

/**
 * Page chrome for TinyCMS screens. The top bar is the app shell; this is the
 * page body from the Ink system (src/styles/ink.css).
 */
export const CmsPage: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, actions, children }) => {
  return (
    <div className="ink-body">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="ink-h1">{title}</h1>
          {subtitle ? <div className="ink-lede">{subtitle}</div> : null}
        </div>
        {actions ? <div className="ink-row-actions">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
};

/** 260px label + controls, the Site and Content settings pattern. */
export const InkSection: React.FC<{
  title: string;
  description?: string;
  id?: string;
  children: React.ReactNode;
}> = ({ title, description, id, children }) => (
  <section className="ink-section" id={id}>
    <div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    <div style={{ minWidth: 0 }}>{children}</div>
  </section>
);

export type ArticleTone = 'live' | 'review' | 'draft' | 'scheduled';

/** Live / needs review / draft / scheduled, matching the library chips. */
export function articleStatus(
  state:
    | {
        live?: unknown;
        scheduled?: unknown;
        hasUnpublishedChanges?: boolean;
      }
    | null
    | undefined
): { label: string; tone: ArticleTone } {
  if (state?.scheduled) return { label: 'Scheduled', tone: 'scheduled' };
  if (state?.live && state.hasUnpublishedChanges) return { label: 'Needs review', tone: 'review' };
  if (state?.live) return { label: 'Live', tone: 'live' };
  return { label: 'Draft', tone: 'draft' };
}

const TONE_COLOR: Record<ArticleTone, string> = {
  live: 'var(--ink-green)',
  review: 'var(--ink-amber)',
  draft: 'var(--ink-ink3)',
  scheduled: 'var(--ink-accent)',
};

export const StatusDot: React.FC<{ tone: ArticleTone; label: string }> = ({ tone, label }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      font: '500 13px/1 var(--ink-font)',
      color: TONE_COLOR[tone] === 'var(--ink-ink3)' ? 'var(--ink-ink2)' : 'var(--ink-ink)',
    }}
  >
    <span className="ink-dot" style={{ background: TONE_COLOR[tone] }} />
    {label}
  </span>
);

export const Panel: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({
  title,
  children,
  className = '',
}) => (
  <Card className={`p-4 bg-panel border border-cmp-line text-cmp-ink ${className}`}>
    {title ? (
      <h2 className="text-xs font-semibold uppercase tracking-wide text-cmp-ink-3 mb-3">{title}</h2>
    ) : null}
    {children}
  </Card>
);

export const formatWhen = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/** Draft / Live / Live + changes / Scheduled, in one badge set. */
export const StateBadges: React.FC<{ state: ItemState }> = ({ state }) => (
  <span className="inline-flex flex-wrap gap-1.5">
    {!state.live && !state.scheduled ? <Badge variant="outline">Draft</Badge> : null}
    {state.live ? <Badge>Live v{state.live.version}</Badge> : null}
    {state.live && state.hasUnpublishedChanges ? (
      <Badge variant="secondary">Unpublished changes</Badge>
    ) : null}
    {state.scheduled ? (
      <Badge variant="secondary">
        v{state.scheduled.version} scheduled {formatWhen(state.scheduled.at)}
      </Badge>
    ) : null}
  </span>
);

export const FindingsList: React.FC<{ findings: Finding[]; empty?: string }> = ({
  findings,
  empty = 'No findings.',
}) => {
  if (!findings.length) return <p className="text-sm text-cmp-ink-2">{empty}</p>;
  const ordered = [...findings].sort(
    (a, b) => Number(b.severity === 'blocking') - Number(a.severity === 'blocking')
  );
  return (
    <ul className="space-y-2">
      {ordered.map((f, i) => (
        <li key={i} className="text-sm">
          <span
            className={`mr-2 inline-block rounded px-1.5 text-[10px] font-semibold uppercase ${
              f.severity === 'blocking'
                ? 'bg-error-soft text-error'
                : 'bg-warning-soft text-warning'
            }`}
          >
            {f.severity === 'blocking' ? 'Blocks' : 'Warning'}
          </span>
          {f.quote ? (
            <span>
              <span className="line-through text-cmp-ink-3">{f.quote}</span>
              {f.suggestion ? <span className="text-cmp-ink"> → {f.suggestion}</span> : null}
              <span className="block text-xs text-cmp-ink-2">
                {f.message}
                {f.rule ? ` (Rule: ${f.rule})` : ''}
              </span>
            </span>
          ) : (
            <span className="text-cmp-ink">
              {f.message || f.code}
              {f.path ? <span className="text-xs text-cmp-ink-3"> · {f.path}</span> : null}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
};

/** Approval history for one item (or the whole account when subjectId is omitted). */
export const ApprovalHistory: React.FC<{
  kind?: Kind;
  subjectId?: string;
  limit?: number;
  variant?: 'ink';
}> = ({ kind, subjectId, limit = 25, variant }) => {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_APPROVALS, {
    variables: { accountId, kind, subjectId, limit },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const rows: any[] = data?.tinycmsApprovals || [];
  if (loading && !rows.length) return <Loader label="Loading" className="text-sm text-cmp-ink-2" />;
  if (!rows.length) return <p className="text-sm text-cmp-ink-2">No publish activity yet.</p>;
  if (variant === 'ink') {
    return (
      <div>
        {rows.map((r, i) => {
          const when = r.recordedAt
            ? new Date(r.recordedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })
            : '—';
          const verb =
            r.action === 'publish' ? 'Published' : r.action === 'unpublish' ? 'Reverted' : r.action;
          const what = r.versionNumber ? `${verb} theme v${r.versionNumber}` : verb;
          const live = i === 0 && r.outcome === 'accepted' && r.action === 'publish';
          const tag = r.outcome !== 'accepted' ? 'Refused' : live ? 'Live' : '';
          const tagColor =
            r.outcome !== 'accepted'
              ? 'var(--ink-red)'
              : live
                ? 'var(--ink-green)'
                : 'var(--ink-ink3)';
          return (
            <div className="ink-site-hist" key={r.id}>
              <time>{when}</time>
              <span>
                {what}{' '}
                <span style={{ fontWeight: 400, color: 'var(--ink-ink3)' }}>· {r.approvedBy}</span>
              </span>
              <span style={{ font: '500 12.5px var(--ink-font)', color: tagColor }}>{tag}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-cmp-line">
      {rows.map((r) => (
        <li key={r.id} className="py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={r.outcome === 'accepted' ? 'default' : 'destructive'}>
              {r.outcome === 'accepted' ? r.action : `${r.action} refused`}
            </Badge>
            {!subjectId ? <span className="text-xs text-cmp-ink-3">{r.kind}</span> : null}
            {r.versionNumber ? <span className="text-cmp-ink">v{r.versionNumber}</span> : null}
            <span className="text-cmp-ink-2">
              {r.action === 'unpublish' ? 'requested by' : 'approved by'} {r.approvedBy}
            </span>
            <span className="text-xs text-cmp-ink-3">
              {formatWhen(r.recordedAt)} · {r.via === 'ui' ? 'website' : 'agent'}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-cmp-ink-3">
            {r.scheduledFor ? `For ${formatWhen(r.scheduledFor)} · ` : ''}
            version {String(r.contentHash).slice(0, 10)}
            {r.refusalReason ? ` · refused: ${r.refusalReason.replace(/_/g, ' ')}` : ''}
            {r.overrideReason ? ` · override: ${r.overrideReason}` : ''}
            {r.note ? ` · ${r.note}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Open a preview of the working copy in a new tab. */
export const PreviewButton: React.FC<{ articleId?: string; pageId?: string; label?: string }> = ({
  articleId,
  pageId,
  label = 'Preview',
}) => {
  const { accountId } = useAccount();
  const [error, setError] = useState('');
  const [create, { loading }] = useMutation(CREATE_TINYCMS_PREVIEW);
  const open = async () => {
    setError('');
    // Open synchronously so the popup isn't blocked, then point it at the URL.
    const win = window.open('', '_blank');
    try {
      const { data } = await create({ variables: { accountId, articleId, pageId } });
      const url = data?.createTinycmsPreview?.url;
      if (win && url) win.location.href = url;
    } catch (err: any) {
      win?.close();
      setError(err.message);
    }
  };
  return (
    <span className="inline-flex flex-col">
      <Button variant="outline" size="sm" onClick={open} disabled={loading || !accountId}>
        <Eye /> {label} <ExternalLink className="size-3" />
      </Button>
      {error ? <span className="mt-1 text-xs text-error max-w-xs">{error}</span> : null}
    </span>
  );
};

/**
 * Publish / schedule / unpublish for one item. The member's click is the
 * approval, for the version whose hash this screen is showing; the server
 * refuses if anything changed since, or if blocking findings remain (unless an
 * override reason is given here).
 */
export const PublishPanel: React.FC<{
  kind: Kind;
  id?: string;
  workingHash: string;
  state: ItemState;
  blocking: number;
  dirty?: boolean;
  onDone: () => void;
}> = ({ kind, id, workingHash, state, blocking, dirty = false, onDone }) => {
  const { accountId } = useAccount();
  const [when, setWhen] = useState('');
  const [override, setOverride] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [publish, { loading }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [unpublish, { loading: unpublishing }] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const nothingToApprove = !state.hasUnpublishedChanges && !state.scheduled;

  const run = async (action: 'publish' | 'schedule') => {
    setMessage(null);
    const scheduledFor = action === 'schedule' ? new Date(when).toISOString() : undefined;
    const label =
      action === 'publish'
        ? 'Publish this version now?'
        : `Schedule this version for ${formatWhen(scheduledFor)}?`;
    if (
      !window.confirm(
        `${label}\n\nVersion ${workingHash.slice(0, 10)} — your approval is recorded with your name and the time.`
      )
    )
      return;
    try {
      const { data } = await publish({
        variables: {
          accountId,
          kind,
          id,
          action,
          contentHash: workingHash,
          scheduledFor,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = data?.publishTinycmsContent;
      if (r?.ok && r.unchanged)
        setMessage({ tone: 'ok', text: 'Already live — nothing to publish.' });
      else if (r?.ok) {
        setMessage({
          tone: 'ok',
          text: action === 'publish' ? `Published v${r.version}.` : `Scheduled v${r.version}.`,
        });
        setOverride('');
      } else {
        setMessage({ tone: 'error', text: `Refused: ${r?.message || r?.refused}` });
      }
      onDone();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const takeDown = async () => {
    const reason = window.prompt(
      kind === 'site'
        ? 'Revert the live site to its default theme? Reason (optional):'
        : 'Take this off the live site? Reason (optional):'
    );
    if (reason === null) return;
    setMessage(null);
    try {
      const { data } = await unpublish({
        variables: { accountId, kind, id, reason: reason || undefined },
      });
      const r = data?.unpublishTinycmsContent;
      setMessage({
        tone: 'ok',
        text: r?.unchanged ? 'Nothing was live.' : `Took down v${r?.removedVersion}.`,
      });
      onDone();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  return (
    <div className="space-y-3">
      <StateBadges state={state} />
      <p className="text-xs text-cmp-ink-3">Working version {workingHash.slice(0, 10)}</p>
      {dirty ? <p className="text-xs text-warning">Save your changes before publishing.</p> : null}
      {blocking > 0 ? (
        <div className="space-y-1">
          <p className="text-xs text-error">
            {blocking} blocking finding{blocking === 1 ? '' : 's'}. Fix them, or give a reason to
            publish anyway:
          </p>
          <input
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder="Override reason (recorded)"
            className="w-full px-2 py-1.5 text-sm bg-form-dark text-cmp-ink rounded border border-cmp-line-2"
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => run('publish')}
          disabled={loading || dirty || nothingToApprove || (blocking > 0 && !override.trim())}
        >
          <Send /> Publish now
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="px-2 py-1.5 text-sm bg-form-dark text-cmp-ink rounded border border-cmp-line-2"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => run('schedule')}
          disabled={loading || dirty || !when || (blocking > 0 && !override.trim())}
        >
          <CalendarClock /> Schedule
        </Button>
      </div>
      {state.live || state.scheduled ? (
        <Button size="sm" variant="ghost" onClick={takeDown} disabled={unpublishing}>
          <EyeOff /> {kind === 'site' ? 'Revert to default theme' : 'Unpublish'}
        </Button>
      ) : null}
      {message ? (
        <p className={`text-sm ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {message.text}
        </p>
      ) : null}
    </div>
  );
};
